/**
 * Private share links: a hidden page (Settings → pages) stays readable by
 * whoever holds the link `<page URL>?share=<key>`.
 *
 * Only the key's SHA-256 hash and an optional expiry live in the public
 * site.json (`share: { hash, expires }`); the key itself is kept by the admin
 * in the private bucket. A new key kills every old link, and `share: null`
 * turns sharing off. nginx marks any `?share=` request noindex.
 */
import { SITE_URL } from "./identity";

export const SHARE_PARAM = "share";
const STORE_KEY = "pp-share-key";

/** A fresh random key: 16 bytes, base64url (22 characters). */
export function newShareKey() {
	const bytes = crypto.getRandomValues(new Uint8Array(16));
	return btoa(String.fromCharCode(...bytes))
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=+$/, "");
}

/** Hex SHA-256 of `key`, the value stored in site.json. */
export async function hashShareKey(key) {
	const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(key));
	return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** `path` on the live site, opened with `key`. */
export const shareUrl = (path, key) => `${SITE_URL}${path}?${SHARE_PARAM}=${key}`;

/**
 * The visitor's share key, if any. A key in the URL is remembered on this
 * browser and taken out of the address bar (and so out of analytics and
 * copy-pasted URLs); later visits use the remembered key.
 * @returns {string | null}
 */
export function captureShareKey() {
	try {
		const url = new URL(window.location.href);
		const fromUrl = url.searchParams.get(SHARE_PARAM);
		if (!fromUrl) return localStorage.getItem(STORE_KEY);
		localStorage.setItem(STORE_KEY, fromUrl);
		url.searchParams.delete(SHARE_PARAM);
		window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
		return fromUrl;
	} catch {
		return null;
	}
}

/**
 * True when `key` opens the link described by `share` ({ hash, expires }
 * from site.json) at time `now`.
 * @param {{ hash?: string, expires?: string | null } | null | undefined} share
 * @param {string | null} key
 */
export async function shareGranted(share, key, now = Date.now()) {
	if (!share?.hash || !key) return false;
	if (share.expires && now > Date.parse(share.expires)) return false;
	try {
		return (await hashShareKey(key)) === share.hash;
	} catch {
		return false;
	}
}
